/**
 * Locale + URL configuration for the landing site.
 *
 * Dependency-free on purpose: imported by the app, by vite.config.ts and by
 * the prerender step, so it must not pull in React or i18next.
 */

/** Same set as the platform (`packages/i18n/src/locales`); a test enforces it. */
export const LOCALES = [
  "en",
  "pt-BR",
  "pt-PT",
  "fr",
  "de",
  "es",
  "it",
] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/** Same key as the platform, so the explicit choice reads the same everywhere. */
export const LANGUAGE_STORAGE_KEY = "thyra-language";

type LocaleMeta = {
  /** URL segment: `/pt-br/about`. The default locale has no segment. */
  slug: string;
  /** Language name in that language, shown in the switcher. */
  label: string;
  /** Compact label for the switcher trigger. */
  short: string;
  /** Open Graph locale (`og:locale`). */
  og: string;
};

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: { slug: "", label: "English", short: "EN", og: "en_US" },
  "pt-BR": {
    slug: "pt-br",
    label: "Português (Brasil)",
    short: "PT-BR",
    og: "pt_BR",
  },
  "pt-PT": {
    slug: "pt-pt",
    label: "Português (Portugal)",
    short: "PT-PT",
    og: "pt_PT",
  },
  fr: { slug: "fr", label: "Français", short: "FR", og: "fr_FR" },
  de: { slug: "de", label: "Deutsch", short: "DE", og: "de_DE" },
  es: { slug: "es", label: "Español", short: "ES", og: "es_ES" },
  it: { slug: "it", label: "Italiano", short: "IT", og: "it_IT" },
};

/**
 * Routes that exist in every locale. Anything else (legal pages, redirect
 * stubs) is English-only and never gets a locale prefix.
 */
export const LOCALIZED_ROUTES = [
  "/",
  "/business",
  "/about",
  "/team",
  "/contact",
] as const;

/**
 * Sections whose root AND every sub-path exist in each locale
 * (`/blog`, `/blog/tag/x`, `/blog/my-post`, `/blog/rss.xml`). Arbitrary
 * paths are never localized: only these exact prefixes.
 */
export const LOCALIZED_SECTIONS = ["/blog"] as const;

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

export function isLocalizedRoute(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return (
    (LOCALIZED_ROUTES as readonly string[]).includes(path) ||
    LOCALIZED_SECTIONS.some(
      (section) => path === section || path.startsWith(`${section}/`),
    )
  );
}

/** Locale whose URL segment is `slug` (case-insensitive); never the default. */
export function localeFromSlug(slug: string): Locale | undefined {
  const lower = slug.toLowerCase();
  if (!lower) return undefined;
  return LOCALES.find(
    (l) => l !== DEFAULT_LOCALE && LOCALE_META[l].slug === lower,
  );
}

/** Splits `/pt-br/about` into `{ locale: "pt-BR", path: "/about" }`. */
export function splitLocalePath(pathname: string): {
  locale: Locale;
  path: string;
} {
  const normalized = normalizePathname(pathname);
  const segment = normalized.split("/")[1]?.toLowerCase() ?? "";
  const locale = LOCALES.find(
    (l) => l !== DEFAULT_LOCALE && LOCALE_META[l].slug === segment,
  );
  if (!locale) return { locale: DEFAULT_LOCALE, path: normalized };
  return {
    locale,
    path: normalizePathname(normalized.slice(segment.length + 1)),
  };
}

/**
 * Prefixes an unprefixed site path (`/about`, `/#pricing`) with the locale
 * segment. Paths outside LOCALIZED_ROUTES are returned unchanged.
 */
export function localizePath(href: string, locale: Locale): string {
  const cut = href.search(/[?#]/);
  const pathname = cut === -1 ? href : href.slice(0, cut);
  const suffix = cut === -1 ? "" : href.slice(cut);
  if (locale === DEFAULT_LOCALE || !isLocalizedRoute(pathname)) return href;
  const path = normalizePathname(pathname);
  return `/${LOCALE_META[locale].slug}${path === "/" ? "" : path}${suffix}`;
}

/**
 * Maps one BCP 47 tag to a supported locale.
 *
 * Self-contained (no outer references) because
 * buildLocaleRedirectScript() inlines its source into the page.
 */
export function matchLocaleTag(
  tag: string | null | undefined,
  locales: readonly string[],
): string | undefined {
  if (!tag) return undefined;
  const lower = String(tag).trim().toLowerCase().replace(/_/g, "-");
  for (let i = 0; i < locales.length; i++) {
    if (locales[i]!.toLowerCase() === lower) return locales[i];
  }
  const base = lower.split("-")[0];
  // Bare "pt" follows the platform (pt-BR); other regions use European norm.
  if (base === "pt") return lower === "pt" ? "pt-BR" : "pt-PT";
  for (let j = 0; j < locales.length; j++) {
    if (locales[j]!.toLowerCase() === base) return locales[j];
  }
  return undefined;
}

/** First supported locale in the browser's ordered preference list. */
export function matchLocale(
  languages: readonly string[] | undefined,
): Locale | undefined {
  for (const tag of languages ?? []) {
    const match = matchLocaleTag(tag, LOCALES);
    if (match) return match as Locale;
  }
  return undefined;
}

/**
 * Inline <head> script: sends a visitor on an unprefixed URL to their
 * language before first paint. An explicit choice (language switcher) wins
 * over the browser languages; a locale-prefixed URL is always respected, so
 * shared links and search results open in the language they point to.
 * Crawlers carry no stored choice, and every locale stays reachable through
 * hreflang links and the sitemap.
 */
export function buildLocaleRedirectScript(): string {
  const slugs = Object.fromEntries(
    LOCALES.map((l) => [l, LOCALE_META[l].slug]),
  );
  return `(function () {
  try {
    var locales = ${JSON.stringify(LOCALES)};
    var slugs = ${JSON.stringify(slugs)};
    var routes = ${JSON.stringify([...LOCALIZED_ROUTES, ...LOCALIZED_SECTIONS])};
    var match = ${matchLocaleTag.toString()};
    var path = location.pathname.replace(/\\/+$/, "") || "/";
    if (routes.indexOf(path) === -1) return;
    var pick;
    try {
      pick = match(localStorage.getItem(${JSON.stringify(LANGUAGE_STORAGE_KEY)}), locales);
    } catch (e) {}
    if (!pick) {
      var langs =
        navigator.languages && navigator.languages.length
          ? navigator.languages
          : [navigator.language];
      for (var i = 0; i < langs.length && !pick; i++) {
        pick = match(langs[i], locales);
      }
    }
    if (!pick || !slugs[pick]) return;
    location.replace(
      "/" + slugs[pick] + (path === "/" ? "" : path) + location.search + location.hash
    );
  } catch (e) {}
})();`;
}
